import { useState, useEffect, useMemo, RefObject } from 'react';
import { convertS3UrlToCloudFrontUrl } from '../aws/awsHelper';
import { ImageMetadata } from '../dto/EntityRepository';
import {
  isBase64,
  getFallbackDeviceType,
  convertToJsonMetadata,
  isDeviceAvailable,
  generateOptimizedUrl,
} from '../util/findAdjustImg';
import { debounce } from '../util/debounce';

export interface OptimizedImageUrlOptions {
  /** 작은 목록 이미지에서는 THUMBNAIL을 우선합니다. 큰 미리보기는 false로 폭별 변형을 사용합니다. */
  preferThumbnail?: boolean;
}

/**
 * originUrl에 대해 meta.json을 사용해 **이미지가 그려지는 영역 너비** 기준으로 리사이즈 URL 반환.
 *
 * B(OptimizedImg)와 동일 정책:
 * - meta 준비 전(http): '' → 호출부가 defaultImg 표시 (원본 선요청 금지)
 * - meta 성공: 리사이즈 URL만 (기본값은 THUMBNAIL 우선)
 * - meta 실패 / 변형 없음: 그때만 원본
 * - base64 / 비HTTP / 빈 URL: 그대로
 *
 * @param originUrl S3 또는 CloudFront 원본 이미지 URL (빈 문자열이면 호출부에서 default 처리)
 * @param containerRef 이미지가 렌더되는 컨테이너 요소 ref. 넣으면 해당 요소의 width 기준으로 선택, 없으면 viewport width 사용
 * @param options THUMBNAIL 우선 여부. false면 컨테이너 폭에 맞는 MOBILE~DESKTOP 변형을 선택
 * @returns 표시용 URL. '' 이면 아직 확정 전 또는 입력 없음 → 호출부가 defaultImg 처리
 */
export const useOptimizedImageUrl = (
  originUrl: string,
  containerRef?: RefObject<HTMLElement | null>,
  options: OptimizedImageUrlOptions = {},
): string => {
  const preferThumbnail = options.preferThumbnail ?? true;
  const cloudFrontUrl = useMemo(
    () => (originUrl === '' ? '' : convertS3UrlToCloudFrontUrl(originUrl)),
    [originUrl]
  );

  const [metadata, setMetadata] = useState<ImageMetadata | null>(null);
  const [metadataFetched, setMetadataFetched] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [resolvedUrl, setResolvedUrl] = useState('');

  const isHttpUrl = useMemo(() => cloudFrontUrl.startsWith('http'), [cloudFrontUrl]);
  const isBase64Img = useMemo(() => isBase64(originUrl), [originUrl]);

  // meta.json fetch
  useEffect(() => {
    setMetadata(null);
    setMetadataFetched(false);

    if (!cloudFrontUrl || isBase64Img || !isHttpUrl) {
      setResolvedUrl(cloudFrontUrl);
      setMetadataFetched(true);
      return;
    }

    // http: meta 전까지 원본을 노출하지 않음 (원본 선요청 방지)
    setResolvedUrl('');

    let cancelled = false;
    const fetchMetadata = async () => {
      try {
        const jsonUrl = cloudFrontUrl.replace(
          /\/[^/?#]+\.(?:jpeg|jpg|png|gif|webp)((?:\?[^#]*)?(?:#.*)?)$/i,
          '/meta.json'
        );
        const response = await fetch(jsonUrl, { cache: 'no-cache' });
        if (!response.ok) throw new Error('Failed to fetch metadata');
        const contentType = response.headers.get('content-type');
        if (!contentType?.includes('application/json')) throw new Error('Response is not JSON');

        const data = await response.json();
        if (!cancelled) {
          setMetadata(convertToJsonMetadata(data));
        }
      } catch {
        if (!cancelled) setMetadata(null);
      } finally {
        if (!cancelled) setMetadataFetched(true);
      }
    };

    fetchMetadata();
    return () => {
      cancelled = true;
    };
  }, [cloudFrontUrl, isBase64Img, isHttpUrl]);

  // 컨테이너 너비 관찰 (이미지 표시 영역 width 기준)
  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return;

    const debouncedUpdate = debounce((entry: ResizeObserverEntry) => {
      const w = entry.contentRect.width;
      setContainerWidth((prev) => (prev !== w ? w : prev));
    }, 200);

    const ro = new ResizeObserver(([entry]) => {
      if (entry) debouncedUpdate(entry);
    });

    ro.observe(el);
    const initialW = el.getBoundingClientRect().width;
    setContainerWidth(initialW);

    return () => {
      ro.disconnect();
      debouncedUpdate.cancel();
    };
  }, [containerRef]);

  // metadata + width 조합으로 URL 계산 (width = 컨테이너 너비 우선, 없으면 viewport)
  useEffect(() => {
    if (isBase64Img || !isHttpUrl) {
      setResolvedUrl(cloudFrontUrl);
      return;
    }
    if (!metadataFetched) {
      setResolvedUrl('');
      return;
    }
    if (!metadata) {
      // meta 실패 시에만 원본
      setResolvedUrl(cloudFrontUrl);
      return;
    }

    // 작은 목록 이미지는 THUMBNAIL 우선, 큰 미리보기는 컨테이너 폭별 변형 사용
    if (preferThumbnail && isDeviceAvailable(metadata, 'THUMBNAIL')) {
      setResolvedUrl(
        generateOptimizedUrl(cloudFrontUrl, 'THUMBNAIL', metadata),
      );
      return;
    }

    const width =
      containerWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 0);
    const deviceType = getFallbackDeviceType(metadata, width);

    if (isDeviceAvailable(metadata, deviceType)) {
      setResolvedUrl(generateOptimizedUrl(cloudFrontUrl, deviceType, metadata));
    } else {
      setResolvedUrl(cloudFrontUrl);
    }
  }, [
    cloudFrontUrl,
    metadata,
    metadataFetched,
    containerWidth,
    isBase64Img,
    isHttpUrl,
    preferThumbnail,
  ]);

  return resolvedUrl;
};
